import React, { useEffect, useRef, useState } from "react";
import { MouthRig } from "./MouthRig";
import XcelioPortrait, { PHOTO_FRAMES, MALE_FRAMES } from "./XcelioPortrait";

// Living portrait (method from the Xcelio face kit): stacked pixel-aligned photos, mouth blended from the voice
// timeline read slightly ahead, plus smile, blinks, eye glances, brow lifts, nods, breathing and a listening lean-in.
const FEATURES = {
  adelaida: { w: 1086, h: 1448, eyes: [[467, 411], [667, 414]], eye: [52, 26], brows: { cx: 567, cy: 372, rx: 175, ry: 22 } },
  richard: { w: 1102, h: 890, eyes: [[473, 341], [660, 334]], eye: [46, 22], brows: { cx: 567, cy: 305, rx: 165, ry: 19 } },
};
const V = "v=14";
// Talking Face Set (8 positions): 1 rest, 2 micro-open, 3 soft open, 4 medium open, 5 rounded O/oo, 6 soft EE/IH, 7 open vowel, 8 warm micro-smile.
// Rig tiles 1-6 = positions 2-7; tile 7 = warm smile (Adelaida) or rest (Richard, whose master photo is the warm smile).
const SPEECH = [0, 1, 2, 3, 4, 5];
const pickFrame = (p, s) => {
  if (p < 0.35) return -1;
  if (p < 1) return 0;
  const lvl = p < 1.9 ? 1 : p < 2.8 ? 2 : 3;
  if (lvl !== s.lvl) {
    const r = Math.random();
    s.var = lvl === 1 ? (r < 0.22 ? 3 : r < 0.4 ? 4 : 1) : lvl === 2 ? (r < 0.28 ? 4 : 2) : 5;
    s.lvl = lvl;
  }
  return s.var;
};
const STILL = { w: 506, h: 400 };
const pct = (v, of) => `${(v / of) * 100}%`;
const eyeMask = (f) => f.eyes.map(([x, y]) => `radial-gradient(ellipse ${pct(f.eye[0], f.w)} ${pct(f.eye[1], f.h)} at ${pct(x, f.w)} ${pct(y, f.h)}, #000 55%, transparent 100%)`).join(",");
const lidMask = (f) => f.eyes.map(([x, y]) => `radial-gradient(ellipse ${pct(f.eye[0] * 1.7, f.w)} ${pct(f.eye[1] * 2.6, f.h)} at ${pct(x, f.w)} ${pct(y - f.eye[1] * 0.3, f.h)}, #000 60%, transparent 100%)`).join(",");
const browMask = (f) => `radial-gradient(ellipse ${pct(f.brows.rx, f.w)} ${pct(f.brows.ry, f.h)} at ${pct(f.brows.cx, f.w)} ${pct(f.brows.cy, f.h)}, #000 45%, transparent 100%)`;

const useCoverBox = (ref, f, focus) => {
  const [box, setBox] = useState({ s: 1, w: 0, h: 0, l: 0, t: 0 });
  useEffect(() => {
    const el = ref.current;
    const fit = () => {
      const W = el.clientWidth; const H = el.clientHeight;
      const s = Math.max(W / f.w, H / f.h);
      setBox({ s, w: f.w * s, h: f.h * s, l: (W - f.w * s) / 2, t: (H - f.h * s) * focus });
    };
    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref, f, focus]);
  return box;
};

const useBlink = () => {
  const [blink, setBlink] = useState(0);
  const now = useRef(null);
  useEffect(() => {
    const timers = []; let off = false; let last = 0;
    const once = (cb) => { last = performance.now(); setBlink(Math.random() < 0.12 ? 0.55 : 1); timers.push(setTimeout(() => { setBlink(0); cb?.(); }, 95 + Math.random() * 90)); };
    const schedule = () => {
      if (off) return;
      timers.push(setTimeout(() => {
        if (off) return;
        if (Math.random() < 0.22) once(() => timers.push(setTimeout(() => once(schedule), 160 + Math.random() * 80)));
        else once(schedule);
      }, 2200 + Math.random() * 3800));
    };
    now.current = () => { if (performance.now() - last > 900) once(); };
    schedule();
    return () => { off = true; timers.forEach(clearTimeout); };
  }, []);
  return [blink, now];
};

// Adelaida and Richard in their own (Xcelio kit) look use the Xcelio face; space outfits keep their rigs.
const kitFrames = (dir) => ({
  neutral: `/coaches/xcelio/${dir}/neutral.jpg`, smile: `/coaches/xcelio/${dir}/smile.jpg`, mid: `/coaches/xcelio/${dir}/mid-mouth.jpg`,
  open: `/coaches/xcelio/${dir}/open-mouth.jpg`, blink: `/coaches/xcelio/${dir}/blink.jpg`,
});
const XCELIO = {
  "/coaches/adelaida.webp": PHOTO_FRAMES, "/coaches/richard.webp": MALE_FRAMES,
  "/coaches/adelaida_calm.webp": PHOTO_FRAMES, "/coaches/richard_calm.webp": MALE_FRAMES,
};
["gym", "atelier", "inner_path", "networker"].forEach((sp) => {
  XCELIO[`/coaches/adelaida_${sp}.webp`] = { ...PHOTO_FRAMES, ...kitFrames(`adelaida_${sp}`) };
  XCELIO[`/coaches/richard_${sp}.webp`] = { ...MALE_FRAMES, ...kitFrames(`richard_${sp}`) };
});

const XcelioFace = ({ frames, audioEl, env, speaking, full, status }) => {
  const amp = useRef(0);
  const talking = useRef(false);
  const live = useRef({});
  live.current = { audioEl, env, speaking };
  useEffect(() => {
    let raf;
    const tick = () => {
      const { audioEl: a, env: e, speaking: sp } = live.current;
      const on = !!(sp && a && !a.paused);
      let v = 0;
      if (on && e?.length) {
        const x = (a.currentTime + 0.06) * 25; const i = Math.floor(x); const fr = x - i;
        v = ((e[i] || 0) * (1 - fr) + (e[i + 1] || 0) * fr) / 100;
        if (v < 0.07) v = 0;
      }
      amp.current = v; talking.current = on;
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);
  return (
    <div className={full ? "absolute inset-y-0 left-1/2 -translate-x-1/2 w-full sm:w-[min(100vw,82vh)] overflow-hidden" : "relative w-40 h-40 sm:w-48 sm:h-48 mx-auto rounded-full overflow-hidden"}
      style={full ? undefined : { boxShadow: "0 0 0 1px rgba(215,187,122,0.35), 0 18px 50px rgba(0,0,0,0.45)" }}
      data-testid={full ? "coach-portrait-full" : "coach-portrait"} data-face="xcelio">
      <XcelioPortrait amplitudeRef={amp} speakingRef={talking} frames={frames} status={status} zoom={full ? 0 : 1.8} />
    </div>
  );
};

export const TalkingPortrait = (props) => {
  const frames = XCELIO[props.who.image];
  return frames ? <XcelioFace frames={frames} {...props} /> : <RigPortrait {...props} />;
};

const RigPortrait = ({ who, audioEl, env, speaking, full = false, status = "idle" }) => {
  const key = who.image.includes("richard") ? "richard" : "adelaida";
  const rigId = who.image.split("/").pop().replace(".webp", "");
  const still = !!who.still;
  const [nat, setNat] = useState(null);
  const f = still ? (nat || STILL) : FEATURES[key];
  const src = (s) => (s ? who.image.replace(".webp", `_${s}.webp?${V}`) : who.image);
  const root = useRef(null); const head = useRef(null); const eyes = useRef(null); const brows = useRef(null); const rig = useRef(null);
  const live = useRef({});
  live.current = { audioEl, env, speaking, status };
  const box = useCoverBox(root, f, full ? 0.2 : 0);
  const sc = useRef(1);
  sc.current = box.s;
  const [blink, blinkNow] = useBlink();

  useEffect(() => {
    let raf;
    const op = SPEECH.map(() => 0);
    const s = { lvl: 0, var: 1, v: 0, slow: 0, talk: 0, nod: 0, brow: 0, smile: 0.5, quiet: 0, lean: 0, gx: 0, gy: 0, tx: 0, ty: 0, glance: 0, hit: 0, tilt: 0, tiltT: 0, nextTilt: 0, was: false, pause: false, settle: 0 };
    const t0 = performance.now();
    const tick = (now) => {
      const t = (now - t0) / 1000;
      const { audioEl: a, env: e, speaking: sp, status: st } = live.current;
      const on = sp && a && !a.paused;
      let target = 0;
      if (on && e?.length) {
        const x = (a.currentTime + 0.06) * 25; const i = Math.floor(x); const fr = x - i;
        target = ((e[i] || 0) * (1 - fr) + (e[i + 1] || 0) * fr) / 100;
        if (target < 0.07) target = 0;
      }
      s.v += (target - s.v) * (target > s.v ? 0.5 : target < 0.03 ? 0.42 : 0.24);
      s.slow += (s.v - s.slow) * 0.04;
      s.talk += ((on ? 1 : 0) - s.talk) * 0.03;
      const p = Math.min(4, s.v * 5);
      const pick = pickFrame(p, s);
      if (pick <= 0) s.lvl = 0;

      op.forEach((o, k) => {
        op[k] = o + ((k === pick ? 1 : 0) - o) * (k === pick ? 0.5 : 0.38);

      });

      if (s.v > 0.06) s.quiet = now;
      const pause = on && now - s.quiet > 380;
      if (on && !s.was) { blinkNow.current?.(); s.brow = Math.max(s.brow, 0.5); }
      if (pause && !s.pause) { s.settle = 1; if (Math.random() < 0.6) blinkNow.current?.(); }
      s.was = !!on; s.pause = pause; s.settle *= 0.95;
      const smileT = st === "listening" ? 0.9 : pause ? 0.7 : on ? 0 : st === "thinking" ? 0.35 : 0.55;
      s.smile += (smileT - s.smile) * (smileT > s.smile ? 0.035 : 0.16);
      rig.current?.set(op, s.smile);

      if (on && s.v - s.slow > 0.22 && now - s.hit > 520) {
        s.hit = now; s.nod = Math.min(1.4, s.nod + 0.8 + Math.random() * 0.4);
        if (Math.random() < 0.55) s.brow = Math.min(1.3, s.brow + 0.9);
      }
      s.nod *= 0.92; s.brow *= 0.955;
      s.lean += ((st === "listening" ? 1 : 0) - s.lean) * 0.025;
      const breathe = Math.sin(t * 1.55) * 0.5 + 0.5;
      if (now > s.nextTilt) { s.tiltT = (Math.random() - 0.5) * (on ? 1.6 : 1.1) + s.lean * 0.6; s.nextTilt = now + 2500 + Math.random() * 4500; }
      s.tilt += (s.tiltT - s.tilt) * 0.012;
      const k = full ? 1 : 0.4;
      const hx = ((Math.sin(t * 0.9) * 1.8 + Math.sin(t * 2.3) * 0.6 + Math.sin(t * 0.41) * 0.7) * s.talk + Math.sin(t * 0.37) * 1.2 * s.lean) * k;
      const hy = (s.nod * 3.2 - breathe * 1.6 + s.lean * 2 + s.settle * 1.4) * k;
      const hr = (Math.sin(t * 0.7 + 1) * 0.7 + Math.sin(t * 1.9) * 0.2) * s.talk + s.tilt + s.nod * 0.25;
      const hs = 1 + breathe * 0.004 + s.lean * 0.018 + s.v * 0.004;
      if (head.current) head.current.style.transform = `translate3d(${hx.toFixed(2)}px,${hy.toFixed(2)}px,0) rotate(${(hr * k).toFixed(3)}deg) scale(${hs.toFixed(4)})`;

      if (now > s.glance) {
        const roam = st === "thinking" ? 1 : on ? 0.35 : 0.7;
        s.tx = Math.random() < 0.5 ? 0 : (Math.random() - 0.5) * 3.6 * roam;
        s.ty = st === "thinking" && Math.random() < 0.6 ? -1.6 : (Math.random() - 0.5) * 1.2 * roam;
        s.glance = now + 700 + Math.random() * (on ? 2600 : 1800);
      }
      s.gx += (s.tx - s.gx) * 0.35; s.gy += (s.ty - s.gy) * 0.35;
      const z = sc.current * (f.w / 848);
      if (eyes.current) eyes.current.style.transform = `translate3d(${(s.gx * z).toFixed(2)}px,${(s.gy * z).toFixed(2)}px,0)`;
      if (brows.current) brows.current.style.transform = `translate3d(0,${(-(s.brow * 4.2 + s.lean * 1.5) * z).toFixed(2)}px,0)`;
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [full, f, blinkNow]);

  const layer = "absolute inset-0 w-full h-full select-none pointer-events-none";
  const shell = full ? "absolute inset-y-0 left-1/2 -translate-x-1/2 w-full sm:w-[min(100vw,82vh)] overflow-hidden"
    : "relative w-40 h-40 sm:w-48 sm:h-48 mx-auto rounded-full overflow-hidden transition-[box-shadow] duration-500";
  return (
    <div ref={root} className={shell} data-testid={full ? "coach-portrait-full" : "coach-portrait"}
      style={full ? undefined : { boxShadow: speaking ? "0 0 0 4px rgba(214,190,130,0.35), 0 0 50px rgba(214,190,130,0.35)" : "0 0 0 1px rgba(214,190,130,0.3)" }}>
      <div ref={head} className="absolute" style={{ left: box.l, top: box.t, width: box.w, height: box.h, transformOrigin: "50% 62%", willChange: "transform" }}>
        <img src={src()} alt={who.name} draggable={false} className={layer} data-testid="coach-portrait-base"
          onLoad={still ? (e) => setNat((n) => n || { w: e.target.naturalWidth, h: e.target.naturalHeight }) : undefined} />
        <MouthRig ref={rig} who={rigId} version={V} />
        {!still && <>
        <img ref={eyes} src={src()} alt="" aria-hidden draggable={false} className={layer} style={{ WebkitMaskImage: eyeMask(f), maskImage: eyeMask(f) }} />
        <img ref={brows} src={src()} alt="" aria-hidden draggable={false} className={layer} style={{ WebkitMaskImage: browMask(f), maskImage: browMask(f) }} />
        <img src={src("blink")} alt="" aria-hidden draggable={false} className={layer}
          style={{ opacity: blink, transition: blink ? "opacity 45ms ease-in" : "opacity 80ms ease-out", WebkitMaskImage: lidMask(f), maskImage: lidMask(f) }} data-testid="coach-portrait-blink" />
        </>}
      </div>
    </div>
  );
};
